const NOMINATIM_URL = 'https://nominatim.openstreetmap.org/search';

const SUPABASE_URL = String(import.meta.env.VITE_SUPABASE_URL || '').replace(/\/$/, '');
const SUPABASE_KEY = String(
  import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ||
  import.meta.env.VITE_SUPABASE_ANON_KEY ||
  ''
).trim();

export async function resolveMissingPoints(points, onProgress = () => {}) {
  if (!Array.isArray(points)) return [];

  const result = [];

  for (let i = 0; i < points.length; i++) {
    const point = { ...points[i] };

    if (hasValidCoordinates(point)) {
      result.push(normalizePointCoordinates(point));
      continue;
    }

    if (point.mapsUrl) {
      onProgress(`Otwieram aktywny link ${i + 1}/${points.length}: ${point.name || 'bez nazwy'}`);

      let linkResult = null;
      let linkError = '';

      try {
        linkResult = await resolveMapsUrl(point.mapsUrl);
      } catch (error) {
        linkError = error?.message || String(error);
      }

      if (linkResult && isValidCoordinatePair(linkResult.lat, linkResult.lon)) {
        result.push(applyResolvedCoordinates(point, linkResult, {
          source: 'Współrzędne odczytane z aktywnego linku Google Maps',
        }));
        continue;
      }

      // Niektóre skrócone linki Google otwierają wyłącznie widok mapy:
      // /maps/place//@lat,lon,... bez pinezki i bez !3d...!4d....
      // Wtedy @lat,lon jest tylko środkiem widoku, więc nie zapisujemy go jako GPS.
      // Link został jednak otwarty; jako bezpieczny fallback wyszukujemy dokładną
      // nazwę przystanku razem z pełnym adresem z PDF.
      onProgress(`Link nie zawiera pinezki — wyszukuję nazwę i adres ${i + 1}/${points.length}: ${point.name || 'bez nazwy'}`);

      try {
        const geocoded = await geocodePoint(point, linkResult?.hint || null);
        if (geocoded && isValidCoordinatePair(geocoded.lat, geocoded.lon)) {
          result.push(applyResolvedCoordinates(point, geocoded, {
            source: 'Aktywny link otwarto, ale zawierał tylko widok mapy; GPS ustalono z nazwy i adresu przystanku',
            resolvedMapsUrl: linkResult?.finalUrl || point.mapsUrl,
            sourceType: 'google-link-address-fallback',
          }));
          continue;
        }

        const reason = linkResult?.message || linkError || 'link nie zawierał docelowych współrzędnych';
        result.push({
          ...point,
          lat: null,
          lon: null,
          resolvedMapsUrl: linkResult?.finalUrl || point.mapsUrl,
          resolveError: `Aktywny link został otwarty, ale nie zawierał pinezki (${reason}). Nie znaleziono również dokładnej nazwy i adresu.`,
        });
      } catch (error) {
        const reason = linkResult?.message || linkError || 'link nie zawierał docelowych współrzędnych';
        result.push({
          ...point,
          lat: null,
          lon: null,
          resolvedMapsUrl: linkResult?.finalUrl || point.mapsUrl,
          resolveError: `Aktywny link został otwarty, ale nie zawierał pinezki (${reason}). Wyszukiwanie nazwy i adresu: ${error.message}`,
        });
      }
      continue;
    }

    onProgress(`Geokoduję adres ${i + 1}/${points.length}: ${point.name || 'bez nazwy'}`);
    try {
      const coordinates = await geocodePoint(point);
      if (coordinates && isValidCoordinatePair(coordinates.lat, coordinates.lon)) {
        result.push(applyResolvedCoordinates(point, coordinates));
      } else {
        result.push({ ...point, lat: null, lon: null, resolveError: 'Nie znaleziono współrzędnych nazwy i adresu.' });
      }
    } catch (error) {
      result.push({ ...point, lat: null, lon: null, resolveError: `Adres: ${error.message}` });
    }
  }

  return result;
}

function applyResolvedCoordinates(point, coordinates, overrides = {}) {
  const lat = toCoordinateNumber(coordinates.lat);
  const lon = toCoordinateNumber(coordinates.lon);

  return {
    ...point,
    lat,
    lon,
    query: `${lat.toFixed(7)}, ${lon.toFixed(7)}`,
    searchQueries: [`${lat.toFixed(7)}, ${lon.toFixed(7)}`],
    sourceType: overrides.sourceType || coordinates.sourceType || 'geocoded-address',
    source: overrides.source || coordinates.source || point.source || '',
    resolvedDisplayName: coordinates.displayName || '',
    resolvedMapsUrl: overrides.resolvedMapsUrl || coordinates.finalUrl || point.mapsUrl || '',
    resolveError: '',
  };
}

async function resolveMapsUrl(url) {
  const direct = coordsFromUrl(url);
  if (direct) return direct;

  if (!SUPABASE_URL) throw new Error('Brak VITE_SUPABASE_URL w pliku .env.');

  const endpoint = `${SUPABASE_URL}/functions/v1/resolve-map-link`;
  const headers = { 'Content-Type': 'application/json' };
  if (SUPABASE_KEY) headers.apikey = SUPABASE_KEY;

  const response = await fetch(endpoint, {
    method: 'POST',
    headers,
    body: JSON.stringify({ url: String(url || '').trim() }),
  });

  const rawText = await response.text();
  let data = null;
  try {
    data = rawText ? JSON.parse(rawText) : null;
  } catch {
    data = null;
  }

  // 422 oznacza tutaj: link został poprawnie otwarty, ale Google zwrócił
  // tylko widok mapy bez współrzędnych pinezki. To nie jest awaria aplikacji.
  if (response.status === 422) {
    const finalUrl = data?.finalUrl || '';
    return {
      unresolved: true,
      finalUrl,
      hint: viewportCoordsFromUrl(finalUrl),
      message: data?.error || 'Google Maps zwrócił tylko widok mapy bez pinezki.',
    };
  }

  if (!response.ok) {
    const edgeCode = response.headers.get('sb-error-code');
    const message = data?.error || data?.message || rawText || `HTTP ${response.status}`;
    throw new Error(`${message}${edgeCode ? ` [${edgeCode}]` : ''}`);
  }

  if (!isValidCoordinatePair(data?.lat, data?.lon)) {
    const finalUrl = data?.finalUrl || '';
    return {
      unresolved: true,
      finalUrl,
      hint: viewportCoordsFromUrl(finalUrl),
      message: data?.error || 'Funkcja otworzyła link, ale nie zwróciła pinezki.',
    };
  }

  return {
    lat: toCoordinateNumber(data.lat),
    lon: toCoordinateNumber(data.lon),
    sourceType: data.source || 'google-maps-resolved',
    displayName: data.displayName || '',
    finalUrl: data.finalUrl || '',
  };
}

async function geocodePoint(point, hint = null) {
  const queries = buildGeocodingQueries(point);

  for (const query of queries) {
    const coordinates = await geocodeWithNominatim(query, point, hint);
    if (coordinates) return coordinates;
  }
  return null;
}

async function geocodeWithNominatim(query, point, hint) {
  const url = new URL(NOMINATIM_URL);
  url.searchParams.set('format', 'jsonv2');
  url.searchParams.set('limit', '7');
  url.searchParams.set('addressdetails', '1');
  url.searchParams.set('namedetails', '1');
  url.searchParams.set('q', query);

  // Środek widoku z Google służy tylko do podpowiedzi kolejności wyników.
  // Nigdy nie jest zapisywany bezpośrednio jako współrzędne przystanku.
  if (hint && isValidCoordinatePair(hint.lat, hint.lon)) {
    const lat = toCoordinateNumber(hint.lat);
    const lon = toCoordinateNumber(hint.lon);
    const delta = 0.12;
    url.searchParams.set('viewbox', `${lon - delta},${lat + delta},${lon + delta},${lat - delta}`);
    url.searchParams.set('bounded', '0');
  }

  const response = await fetch(url, {
    headers: {
      Accept: 'application/json',
      'Accept-Language': 'pl,en,cs,de,it;q=0.8',
    },
  });
  if (!response.ok) throw new Error(`Nominatim HTTP ${response.status}`);

  const data = await response.json();
  if (!Array.isArray(data) || data.length === 0) return null;

  const ranked = data
    .filter(item => isValidCoordinatePair(item?.lat, item?.lon))
    .map(item => ({ item, score: scoreGeocodingCandidate(item, point, hint) }))
    .sort((a, b) => b.score - a.score);

  const best = ranked[0]?.item;
  if (!best) return null;

  return {
    lat: toCoordinateNumber(best.lat),
    lon: toCoordinateNumber(best.lon),
    sourceType: 'geocoded-name-address',
    source: 'Nazwa i pełny adres z PDF',
    displayName: best.display_name || '',
  };
}

function buildGeocodingQueries(point) {
  const name = cleanText(point?.name);
  const address = normalizeAddressForSearch(point?.address);
  const queries = [];

  // Najpierw zawsze nazwa przystanku + pełny adres.
  // Sam adres jest dopiero dalszym fallbackiem, żeby nie wybierać innej ulicy.
  addUnique(queries, name && address ? `${name}, ${address}` : '');
  addUnique(queries, name && address ? `${address}, ${name}` : '');
  addUnique(queries, name);
  addUnique(queries, address);

  return queries;
}

function normalizeAddressForSearch(value) {
  return cleanText(value)
    .replace(/\b(?:ul|al|pl)\.\s*/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function scoreGeocodingCandidate(candidate, point, hint) {
  const candidateText = normalizeSearchText([
    candidate?.display_name,
    ...Object.values(candidate?.address || {}),
    ...Object.values(candidate?.namedetails || {}),
  ].join(' '));

  const expectedText = normalizeSearchText(`${point?.name || ''} ${point?.address || ''}`);
  const tokens = [...new Set(expectedText.split(' ').filter(token => token.length >= 3))];

  let score = Number(candidate?.importance || 0) * 2;
  for (const token of tokens) {
    if (candidateText.includes(token)) score += /^\d+$/.test(token) ? 4 : 2;
  }

  const expectedPostal = digitsOnly(String(point?.address || '').match(/\b\d{2,3}\s*-?\s*\d{2,3}\b/)?.[0] || '');
  const candidatePostal = digitsOnly(candidate?.address?.postcode || '');
  if (expectedPostal && candidatePostal && expectedPostal === candidatePostal) score += 10;

  if (hint && isValidCoordinatePair(hint.lat, hint.lon)) {
    const km = haversineKm(
      toCoordinateNumber(candidate.lat),
      toCoordinateNumber(candidate.lon),
      toCoordinateNumber(hint.lat),
      toCoordinateNumber(hint.lon),
    );
    if (km <= 0.5) score += 7;
    else if (km <= 2) score += 5;
    else if (km <= 10) score += 2;
    else if (km > 50) score -= 6;
  }

  return score;
}

function coordsFromUrl(url) {
  const decoded = safelyDecode(url);
  const patterns = [
    /!8m2!3d(-?\d+(?:\.\d+)?)!4d(-?\d+(?:\.\d+)?)/g,
    /!3d(-?\d+(?:\.\d+)?)!4d(-?\d+(?:\.\d+)?)/g,
    /[?&](?:q|query|ll|destination|daddr|saddr)=\s*(?:loc:)?\s*(-?\d+(?:\.\d+)?)\s*[, ]\s*(-?\d+(?:\.\d+)?)/gi,
  ];

  for (const pattern of patterns) {
    const matches = [...decoded.matchAll(pattern)];
    for (let i = matches.length - 1; i >= 0; i--) {
      const lat = toCoordinateNumber(matches[i][1]);
      const lon = toCoordinateNumber(matches[i][2]);
      if (isValidCoordinatePair(lat, lon)) {
        return { lat, lon, sourceType: 'google-maps-link', finalUrl: decoded };
      }
    }
  }

  // /@lat,lon celowo nie jest tutaj uznawane za docelowy GPS.
  return null;
}

function viewportCoordsFromUrl(url) {
  const decoded = safelyDecode(url);
  const matches = [...decoded.matchAll(/@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)(?:,|\/|$)/g)];
  for (let i = matches.length - 1; i >= 0; i--) {
    const lat = toCoordinateNumber(matches[i][1]);
    const lon = toCoordinateNumber(matches[i][2]);
    if (isValidCoordinatePair(lat, lon)) return { lat, lon };
  }
  return null;
}

function safelyDecode(value) {
  let decoded = String(value || '')
    .replace(/&amp;/g, '&')
    .replace(/\\u003d/g, '=')
    .replace(/\\u0026/g, '&')
    .replace(/\\x3d/g, '=')
    .replace(/\\x26/g, '&');

  for (let i = 0; i < 5; i++) {
    try {
      const next = decodeURIComponent(decoded);
      if (next === decoded) break;
      decoded = next;
    } catch {
      break;
    }
  }
  return decoded;
}

function hasValidCoordinates(point) {
  return point && isValidCoordinatePair(point.lat, point.lon);
}

function normalizePointCoordinates(point) {
  return { ...point, lat: toCoordinateNumber(point.lat), lon: toCoordinateNumber(point.lon) };
}

function isValidCoordinatePair(latValue, lonValue) {
  if (
    latValue === null || latValue === undefined || latValue === '' ||
    lonValue === null || lonValue === undefined || lonValue === ''
  ) return false;

  const lat = toCoordinateNumber(latValue);
  const lon = toCoordinateNumber(lonValue);
  return Number.isFinite(lat) && Number.isFinite(lon) &&
    lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180 &&
    !(lat === 0 && lon === 0);
}

function toCoordinateNumber(value) {
  return Number(String(value).trim().replace(',', '.'));
}

function cleanText(value) {
  return String(value || '').replace(/\s+/g, ' ').trim();
}

function normalizeSearchText(value) {
  return cleanText(value)
    .toLocaleLowerCase('pl-PL')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function digitsOnly(value) {
  return String(value || '').replace(/\D/g, '');
}

function addUnique(list, value) {
  const clean = cleanText(value);
  if (!clean) return;
  const key = clean.toLocaleLowerCase('pl-PL');
  if (!list.some(item => item.toLocaleLowerCase('pl-PL') === key)) list.push(clean);
}

function haversineKm(lat1, lon1, lat2, lon2) {
  const rad = value => value * Math.PI / 180;
  const earthRadiusKm = 6371;
  const dLat = rad(lat2 - lat1);
  const dLon = rad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(rad(lat1)) * Math.cos(rad(lat2)) * Math.sin(dLon / 2) ** 2;
  return earthRadiusKm * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
